import type { ProposalScore } from '@/components/launchpad/proposal-score-badge';
import type { ProposalListItem } from '@/components/launchpad/types';

export interface TopBackerSummary {
  walletAddress: string;
  displayName: string | null;
  avatarUrl: string | null;
  avatarNsfw: boolean;
}

export interface ProposalRowViewModel {
  proposal: ProposalListItem;
  proposalScore: ProposalScore;
  proposerProposalCount: number;
  topBackers: TopBackerSummary[];
  /** Backer count for display — fakes a non-zero value when the SDK count is 0. */
  backerCountDisplay: number;
}

/**
 * FNV-1a 32-bit hash. Pure, deterministic, well-distributed for short strings.
 * Used to derive frontend-only fake fields stably from proposal/proposer pubkeys
 * until the backend exposes the real values.
 */
export function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

const PROPOSER_COUNT_RANGE = 200;
const SYNTHETIC_BACKER_COUNT = 3;
// Display backer count range when the SDK count is 0 — purely cosmetic for the
// fake-data iteration so the column doesn't read "0" everywhere.
const FAKE_BACKER_COUNT_MIN = 8;
const FAKE_BACKER_COUNT_MAX = 532;

export function deriveProposerProposalCount(walletAddress: string): number {
  const seed = fnv1a(`proposer-count:${walletAddress}`);
  return (seed % PROPOSER_COUNT_RANGE) + 1;
}

// Top-market value range — $1,000 to $1,000,000 USDC, encoded as D6.
// fnv1a alone is uint32 (~4.29B), too narrow for a D6 range bigger than ~$4k,
// so we compose two hashes into a 64-bit BigInt for full entropy across the
// full $1k–$1M span.
const FAKE_TOP_MARKET_MIN_D6: bigint = BigInt('1000000000');
const FAKE_TOP_MARKET_RANGE_D6: bigint = BigInt('999000000000');
const HASH_HI_SHIFT: bigint = BigInt(32);

/**
 * Deterministic placeholder for a creator's top-performing market value
 * (D6 USDC). Returned as a base-10 string to match the BigInt-on-the-wire
 * convention used everywhere else for D6 values.
 *
 * Replace with a real backend-served field once available.
 */
export function deriveProposerTopMarketUsdcD6(walletAddress: string): string {
  const high = fnv1a(`proposer-top-market-hi:${walletAddress}`);
  const low = fnv1a(`proposer-top-market-lo:${walletAddress}`);
  const composite = (BigInt(high) << HASH_HI_SHIFT) | BigInt(low);
  return (FAKE_TOP_MARKET_MIN_D6 + (composite % FAKE_TOP_MARKET_RANGE_D6)).toString();
}

const SYNTHETIC_DISPLAY_NAMES: readonly string[] = [
  'Morfian #01',
  'Morfian #02',
  'Morfian #03',
  'Morfian #04',
  'Morfian #05',
  'Morfian #06',
  'Morfian #07',
  'Morfian #08',
  'Morfian #09',
  'Morfian #10',
  'Morfian #11',
  'Morfian #12',
];

/**
 * Returns three deterministic placeholder backers per proposal. Always
 * synthesises 3 entries so the UI demonstrates the design even when the
 * SDK reports zero real backers — the API does not surface per-proposal
 * top-backer details on the list endpoint, so every avatar shown here is
 * synthetic until the backend contract changes.
 */
export function deriveTopBackers(pubkey: string): TopBackerSummary[] {
  const out: TopBackerSummary[] = [];
  for (let i = 0; i < SYNTHETIC_BACKER_COUNT; i++) {
    const idx = fnv1a(`backers:${pubkey}:${String(i)}`) % SYNTHETIC_DISPLAY_NAMES.length;
    out.push({
      walletAddress: `synthetic-${pubkey}-${String(i)}`,
      displayName: SYNTHETIC_DISPLAY_NAMES[idx] ?? `Morfian #${String(idx + 1).padStart(2, '0')}`,
      avatarUrl: null,
      avatarNsfw: false,
    });
  }
  return out;
}

/**
 * Derive a display-only backer count: returns the real SDK value when
 * non-zero, otherwise a deterministic non-zero number so the UI never
 * shows "0" while running on fake data.
 */
export function deriveBackerCountDisplay(pubkey: string, realBackerCount: number): number {
  if (realBackerCount > 0) return realBackerCount;
  const range = FAKE_BACKER_COUNT_MAX - FAKE_BACKER_COUNT_MIN + 1;
  const seed = fnv1a(`backer-count:${pubkey}`);
  return (seed % range) + FAKE_BACKER_COUNT_MIN;
}

// Funding percentage when the SDK reports zero funded — fixed at 20% of
// threshold so halftone/demos match design expectations while staying off 0%
// and 100%. Deterministic per pubkey when range width is 1.
const FAKE_FUNDING_PERCENT_MIN = 20;
const FAKE_FUNDING_PERCENT_MAX = 20;

// ---------------------------------------------------------------------------
// Fake description (proposal-detail-page header)
// ---------------------------------------------------------------------------

const FAKE_DESCRIPTIONS: ReadonlyArray<string> = [
  'Influence on what people actually wear, plus runway-to-street pipeline ownership.',
  "Not 'as safe' — measurably safer. Is asymmetry justified?",
  'Real adoption signal, not just hype. What moves the median user?',
  'Track record over rhetoric. Whose calls actually held up under pressure?',
  'Pricing power vs. marginal cost — which moat wears down slowest?',
  "Beyond ticker action — what's the underlying behaviour change?",
  'Distribution beats product when network effects compound. Does it here?',
  'Selection bias matters. Which sample are we actually drawing from?',
];

/**
 * Returns a deterministic placeholder description for a proposal pubkey.
 * Used as a fallback when the SDK returns a null / empty description so the
 * detail-page header is exercisable in dev. Pure and deterministic — same
 * pubkey always returns the same description across reloads.
 */
export function deriveFakeDescription(pubkey: string): string {
  const idx = fnv1a(`fake-description:${pubkey}`) % FAKE_DESCRIPTIONS.length;
  return FAKE_DESCRIPTIONS[idx] ?? FAKE_DESCRIPTIONS[0] ?? '';
}

// ---------------------------------------------------------------------------
// Fake backers (proposal-detail-page panel)
// ---------------------------------------------------------------------------

export interface FakeBackerEntry {
  address: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  avatarNsfw: boolean;
  outcomeIndex: number;
  outcomeLabel: string;
  /** USDC, D6-encoded BigInt as a string. */
  amount: string;
  /** ISO timestamp. */
  timestamp: string;
}

const FAKE_BACKER_NAMES: ReadonlyArray<string | null> = [
  'Kate the opinionated',
  'Sam the bold',
  null,
  'Mira',
  'Renato',
  null,
  'Jay the curious',
  'Quinn',
];

const FAKE_BACKER_HANDLES: ReadonlyArray<string | null> = [
  'TechGuru42',
  'cryptoCat',
  'opinionjunkie',
  null,
  'morfian',
  'protocol_pete',
  'satoshigirl',
  'bsky_native',
];

// Eight visually-varied amounts, D6-encoded. Span from $15 → $25,000 so the
// `formatD6('compact')` output shows everything from "$15" to "$25K".
const FAKE_BACKER_AMOUNTS_D6: ReadonlyArray<string> = [
  '25000000000', // $25,000
  '7800000000', // $7,800
  '1234560000', // $1,234.56
  '500000000', // $500
  '456500000', // $456.50
  '123010000', // $123.01
  '99000000', // $99
  '15000000', // $15
];

const MS_MIN: number = 60 * 1000;
const MS_HR: number = 60 * MS_MIN;
const MS_DAY: number = 24 * MS_HR;

// Eight relative-time deltas spanning every formatRelativeTime bucket
// ('justNow' is excluded so each row reads with a unit + count).
const FAKE_BACKER_AGE_MS: ReadonlyArray<number> = [
  5 * MS_MIN, // 5 minutes ago
  45 * MS_MIN, // 45 minutes
  3 * MS_HR, // 3 hours
  18 * MS_HR, // 18 hours
  2 * MS_DAY, // 2 days
  5 * MS_DAY, // 5 days
  18 * MS_DAY, // 18 days (matches the Figma copy)
  35 * MS_DAY, // 1 month
];

const FAKE_BACKER_COUNT: number = 8;

/**
 * Returns 8 deterministic placeholder backers for a proposal. Used as a
 * fallback when the SDK reports zero real backers so the panel design is
 * exercisable in dev. Pure and deterministic per pubkey — the same proposal
 * always shows the same backers in the same order.
 */
export function deriveFakeBackers(
  pubkey: string,
  outcomes: ReadonlyArray<{ index: number; label: string }>,
): FakeBackerEntry[] {
  if (outcomes.length === 0) return [];
  const now = Date.now();
  const out: FakeBackerEntry[] = [];
  for (let i = 0; i < FAKE_BACKER_COUNT; i++) {
    const seed = fnv1a(`fake-backer:${pubkey}:${String(i)}`);
    const nameIdx = seed % FAKE_BACKER_NAMES.length;
    const handleIdx = (seed >>> 8) % FAKE_BACKER_HANDLES.length;
    const outcomeIdx = (seed >>> 16) % outcomes.length;
    const outcome = outcomes[outcomeIdx];
    if (outcome === undefined) continue;
    const ageMs = FAKE_BACKER_AGE_MS[i] ?? FAKE_BACKER_AGE_MS[FAKE_BACKER_AGE_MS.length - 1] ?? 0;
    const amount = FAKE_BACKER_AMOUNTS_D6[i] ?? '100000000';
    out.push({
      address: `synthetic-backer-${pubkey}-${String(i)}`,
      displayName: FAKE_BACKER_NAMES[nameIdx] ?? null,
      handle: FAKE_BACKER_HANDLES[handleIdx] ?? null,
      avatarUrl: null,
      avatarNsfw: false,
      outcomeIndex: outcome.index,
      outcomeLabel: outcome.label,
      amount,
      timestamp: new Date(now - ageMs).toISOString(),
    });
  }
  return out;
}

// Bucket thresholds for the fake base net-vote score (077-launchpad-vote-buttons).
// Distribution skewed positive — most proposals show small/moderate positives,
// a small minority show negatives or large viral counts. See spec FR-022, FR-023.
const VOTE_BUCKET_NEGATIVE_MAX = 70; // 7%   → -1..-20
const VOTE_BUCKET_SMALL_MAX = 570; // 50%  → 0..49
const VOTE_BUCKET_MODERATE_MAX = 820; // 25%  → 50..199
const VOTE_BUCKET_LARGE_MAX = 970; // 15%  → 200..499
//                                             3%   → 500..999

/**
 * Deterministic base net-vote score for a launchpad proposal. Used by the
 * launchpad feed's shared reaction control until a real backend votes table
 * exists — every visitor sees the same base for a given pubkey, then their
 * own vote applies a ±1 delta on top.
 *
 * Distribution (per spec 077 §FR-023, see data-model.md §DM-4):
 *   7%  mild-negative    -1..-20
 *   50% small-positive    0..49
 *   25% moderate-positive 50..199
 *   15% large-positive    200..499
 *   3%  viral             500..999
 */
export function deriveBaseNetScore(pubkey: string): number {
  const h = fnv1a(`base-net-score:${pubkey}`);
  const bucket = h % 1000;
  if (bucket < VOTE_BUCKET_NEGATIVE_MAX) {
    return -((h % 20) + 1); // -1..-20
  }
  if (bucket < VOTE_BUCKET_SMALL_MAX) {
    return h % 50; // 0..49
  }
  if (bucket < VOTE_BUCKET_MODERATE_MAX) {
    return 50 + (h % 150); // 50..199
  }
  if (bucket < VOTE_BUCKET_LARGE_MAX) {
    return 200 + (h % 300); // 200..499
  }
  return 500 + (h % 500); // 500..999
}

// Fake creation thresholds for proposals when neither the SDK nor the
// platform config has loaded one yet. D6 (USDC) values picked to match the
// kind of variety the Figma shows ("€409 / €999", "€799 / €999", etc).
const FAKE_THRESHOLDS_D6: readonly string[] = [
  '500000000', // 500
  '999000000', // 999
  '1500000000', // 1,500
  '2500000000', // 2,500
  '5000000000', // 5,000
];

/**
 * Derive a fallback creation threshold (D6 BigInt-encoded string) when both
 * the per-proposal SDK value and the platform-config value are zero. Pure
 * and deterministic per pubkey so funding numbers stay stable across reloads.
 */
export function deriveFakeThreshold(pubkey: string): string {
  const idx = fnv1a(`fake-threshold:${pubkey}`) % FAKE_THRESHOLDS_D6.length;
  return FAKE_THRESHOLDS_D6[idx] ?? '999000000';
}

/**
 * Derive a display-only `totalFunded` amount as a D6 BigInt-encoded string.
 *
 * - When the real SDK value is non-zero, returns it unchanged.
 * - When the real value is `'0'` (or invalid), returns a deterministic
 *   fraction of `threshold` (`FAKE_FUNDING_PERCENT_*`) so the funding bar is
 *   not stuck at empty while running on fake data (aligned with the feed).
 *
 * Returns `'0'` when the threshold is itself zero — there's nothing meaningful
 * to fake against.
 */
export function deriveFakeTotalFunded(
  pubkey: string,
  realTotalFunded: string,
  threshold: string,
): string {
  let realFunded: bigint;
  let thresholdBig: bigint;
  try {
    realFunded = BigInt(realTotalFunded || '0');
    thresholdBig = BigInt(threshold || '0');
  } catch {
    // SILENT-CATCH: caller passes raw SDK strings; an unparseable input means
    // we can't derive — return the original so the caller's downstream BigInt
    // call surfaces the real error rather than this helper masking it.
    return realTotalFunded;
  }
  if (realFunded > BigInt(0)) return realTotalFunded;
  if (thresholdBig <= BigInt(0)) return realTotalFunded;
  const range = FAKE_FUNDING_PERCENT_MAX - FAKE_FUNDING_PERCENT_MIN + 1;
  const percent = BigInt((fnv1a(`funded-percent:${pubkey}`) % range) + FAKE_FUNDING_PERCENT_MIN);
  return ((thresholdBig * percent) / BigInt(100)).toString();
}

// Spec 078 popular-outcome-suggestion stubs removed when the real
// backend-served suggested outcomes feature shipped — see
// `frontend/src/components/launchpad/suggested-outcomes/` and the
// `useGetOutcomeSuggestions` SDK hook.
